/**
 * Release 2 G3 / G1 helpers (docs/specs/release2-contract.md, lane B): the board "Fields" tab draft, value
 * emptiness (mirrors `asset_missing_field_ids`: blank text and empty option lists are no value) and the
 * tag-creation mode check the API enforces with 403 `tag_creation_restricted`.
 */
import type { CustomField, CustomFieldValueSet, Schemas } from '@/lib/api/types';
import { hasPermission } from '@/lib/shared';

export type FieldPolicy = Schemas['FieldPolicy'];
export type EffectiveFieldPolicy = Schemas['EffectiveFieldPolicy'];
export type BoardFieldPolicies = Schemas['BoardFieldPolicies'];
export type TagCreationMode = Schemas['TagCreationMode'];

/** `inherit` = no row on this board (the nearest ancestor's row, or the field definition, applies). */
export type FieldRule = 'inherit' | 'optional' | 'required' | 'hidden';
export type FieldDraft = { rule: FieldRule; default_value: CustomFieldValueSet | null };
export type PoliciesDraft = Record<string, FieldDraft>;

export function draftFromPolicies(bp: BoardFieldPolicies | undefined): PoliciesDraft {
  const out: PoliciesDraft = {};
  for (const p of bp?.items ?? []) out[p.field_id] = { rule: p.required ? 'required' : p.hidden ? 'hidden' : 'optional', default_value: p.default_value ?? null };
  return out;
}

/**
 * The `setBoardFieldPolicies` body for a draft. A default on an inherited field needs a row of its own, which then
 * carries the inherited required/hidden flags so the default does not change the rule.
 */
export function policiesFromDraft(draft: PoliciesDraft, effective: readonly EffectiveFieldPolicy[], boardId: string): FieldPolicy[] {
  const inherited = new Map(effective.filter((e) => e.source_board_id !== boardId).map((e) => [e.field_id, e]));
  const out: FieldPolicy[] = [];
  for (const [field_id, d] of Object.entries(draft)) {
    const hasDefault = d.default_value != null && !isEmptyValue(d.default_value);
    if (d.rule === 'inherit' && !hasDefault) continue;
    const base = inherited.get(field_id);
    const required = d.rule === 'inherit' ? !!base?.required : d.rule === 'required';
    const hidden = d.rule === 'inherit' ? !!base?.hidden && !required : d.rule === 'hidden';
    out.push({ field_id, required, hidden, default_value: hasDefault ? d.default_value : null });
  }
  return out;
}

export function isEmptyValue(v: CustomFieldValueSet | null | undefined): boolean {
  if (!v) return true;
  if (v.value_option_ids && v.value_option_ids.length) return false;
  if (typeof v.value_text === 'string' && v.value_text.trim() !== '') return false;
  return v.value_number == null && !v.value_date;
}

/** The value-set key a field type writes (one typed key, as the API keeps for defaults). */
export function valueFor(field: Pick<CustomField, 'type'>, raw: string | string[] | null): CustomFieldValueSet | null {
  if (raw == null || raw === '' || (Array.isArray(raw) && raw.length === 0)) return null;
  switch (field.type) {
    case 'number':
      return Number.isFinite(Number(raw)) ? { value_number: Number(raw) } : null;
    case 'date':
      return { value_date: String(raw) };
    case 'select':
      return { value_option_ids: [Array.isArray(raw) ? raw[0]! : raw] };
    case 'multi_select':
      return { value_option_ids: Array.isArray(raw) ? raw : [raw] };
    default:
      return { value_text: String(raw) };
  }
}

/** Short human text for a value (Fields tab default column, inherited hints). */
export function describeValue(field: Pick<CustomField, 'type' | 'options'>, v: CustomFieldValueSet | null | undefined): string {
  if (!v || isEmptyValue(v)) return '';
  if (field.type === 'select' || field.type === 'multi_select') return (v.value_option_ids ?? []).map((id) => field.options?.find((o) => o.id === id)?.label ?? id).join(', ');
  if (field.type === 'number') return String(v.value_number ?? '');
  if (field.type === 'date') return (v.value_date ?? '').slice(0, 10);
  return v.value_text ?? '';
}

/** Workspace-level bits each `tag_creation` mode asks for (mirrors routes/tag-groups.ts). */
export function canCreateTags(mode: TagCreationMode | undefined, mask: number): boolean {
  const need = mode === 'admins' ? 'ADMIN' : mode === 'editors' ? 'MANAGE_BOARD' : 'EDIT_METADATA';
  return hasPermission(mask, need);
}

export const TAG_CREATION_LABEL: Record<TagCreationMode, string> = {
  everyone: 'Everyone who can edit metadata',
  editors: 'Editors (board managers) and admins',
  admins: 'Admins only',
};
